import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Rocket, Fire, ArrowUpRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
const STARTUP_CATEGORIES = ["All", "AI Agents", "AI Infrastructure", "AI Chips", "AI Video", "AI Audio", "AI Coding", "AI Image", "AI Search"];
import { stripHtml } from "@/lib/stripHtml";

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 animate-pulse">
      <div className="flex gap-4 mb-4">
        <div className="w-12 h-12 bg-slate-200 rounded-xl shrink-0" />
        <div className="flex-1">
          <div className="h-4 bg-slate-200 rounded w-1/2 mb-2" />
          <div className="h-3 bg-slate-200 rounded w-1/3" />
        </div>
      </div>
      <div className="h-3 bg-slate-200 rounded w-full mb-2" />
      <div className="h-10 bg-slate-200 rounded-xl" />
    </div>
  );
}

function StartupCard({ startup }) {
  const slug = startup.slug || startup.id;
  return (
    <Link to={`/trending-ai/startups/${slug}`} className="block bg-white border border-slate-200 rounded-2xl p-5 hover:border-blue-300 hover:shadow-md transition-all group">
      <div className="flex items-start gap-4 mb-4">
        <div className="text-4xl shrink-0">{startup.emoji || "🚀"}</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors">{stripHtml(startup.name)}</h3>
            {(startup.isNew || startup.is_new) && <span className="text-[10px] font-bold bg-emerald-500 text-white px-1.5 py-0.5 rounded">NEW</span>}
            {startup.hot && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Fire size={7} weight="fill" /> HOT
              </span>
            )}
          </div>
          <p className="text-xs text-slate-500">{startup.location} · Founded {startup.founded}</p>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed mb-4">{stripHtml(startup.description)}</p>

      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xl font-black text-emerald-700">{startup.funding}</div>
            <div className="text-xs text-emerald-600 font-medium">{startup.round}</div>
          </div>
          <div className="text-right">
            <div className="text-sm font-bold text-slate-700">val. {startup.valuation}</div>
            <div className="text-xs text-slate-400">{startup.category}</div>
          </div>
        </div>
      </div>

      {startup.investors && startup.investors.length > 0 && (
        <div className="mb-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Key Investors</p>
          <div className="flex flex-wrap gap-1">
            {startup.investors.map((inv) => (
              <span key={inv} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{inv}</span>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="text-xs text-slate-400">{startup.website}</span>
        {startup.website && (
          <span className="flex items-center gap-1 text-xs font-medium text-blue-600">
            Visit site <ArrowUpRight size={11} />
          </span>
        )}
      </div>
    </Link>
  );
}

export default function AIStartupsPage() {
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");
  const [sort, setSort] = useState("hot");

  const fetchFn = useCallback(
    (page) => fetchAISection("startups", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-startups",
    getCacheKey: (page) => `ai-startups-p${page}`,
    ttlMs: 15 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;

  const totalFunding = allItems.reduce((s, st) => {
    const num = parseFloat((st.funding || "0").replace(/[$MB]/g, ""));
    return s + (isNaN(num) ? 0 : num);
  }, 0);

  const filtered = allItems
    .filter((s) => {
      const matchCat = cat === "All" || s.category === cat;
      const matchSearch = !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.description || "").toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSearch;
    })
    .sort((a, b) => {
      if (sort === "funding") return parseFloat((b.funding || "0").replace(/[$MB]/g, "")) - parseFloat((a.funding || "0").replace(/[$MB]/g, ""));
      if (sort === "name") return a.name.localeCompare(b.name);
      return (b.hot ? 1 : 0) - (a.hot ? 1 : 0) || ((b.isNew || b.is_new) ? 1 : 0) - ((a.isNew || a.is_new) ? 1 : 0);
    });

  return (
    <>
      <SEO
        title="AI Startups — Track New AI Startups, Funding & Launches · TrendMing"
        description="Discover the hottest AI startups: funding rounds, valuations, investors, and product launches. Track Cognition, Together AI, ElevenLabs, and more."
        canonical="https://trendming.com/trending-ai/startups"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Startups</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Startup Tracker</h1>
          <p className="text-slate-500 text-sm mt-1">Track the most exciting AI startups — funding rounds, product launches, and acquisitions.</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "Startups Tracked", value: allItems.length },
            { label: "Total Funding", value: `$${(totalFunding / 1000).toFixed(1)}B+` },
            { label: "New This Month", value: allItems.filter((s) => s.isNew || s.is_new).length },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-slate-900">{s.value}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search startups…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
            <option value="hot">Trending First</option>
            <option value="funding">Most Funded</option>
            <option value="name">A–Z</option>
          </select>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          {STARTUP_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                cat === c ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((startup) => <StartupCard key={startup.id || startup.slug} startup={startup} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No startups found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load startups</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}
      </div>
    </>
  );
}
